import React from 'react';
import { Star, Quote, User, Calendar, MapPin, ThumbsUp } from 'lucide-react';

const Testimonials = () => {
  const testimonials = [
    {
      id: 1,
      name: 'Mrs. Rajni Patel',
      age: 58,
      location: 'Sikar, Rajasthan',
      rating: 5,
      date: 'March 2024',
      procedure: 'Total Hip Replacement',
      doctor: 'Dr. Rajesh Sharma',
      avatar: '👩',
      testimonial: 'I cannot express how grateful I am to Dr. Sharma and his team. After my hip replacement surgery, I can walk without pain for the first time in years. The entire staff was caring and professional throughout my treatment.',
      highlights: ['Pain-free walking', 'Professional care', 'Excellent staff'],
      verified: true,
      videoAvailable: true
    },
    {
      id: 2,
      name: 'Mr. Amit Kumar',
      age: 32,
      location: 'Delhi, India',
      rating: 5,
      date: 'February 2024',
      procedure: 'ACL Reconstruction',
      doctor: 'Dr. Priya Kumari',
      avatar: '👨',
      testimonial: 'As a cricket player, I was devastated by my ACL injury. Dr. Priya Kumari not only fixed my knee but also guided me through an amazing rehabilitation program. I am back to playing cricket at the competitive level!',
      highlights: ['Back to sports', 'Excellent rehabilitation', 'Expert guidance'],
      verified: true,
      videoAvailable: false
    },
    {
      id: 3,
      name: 'Mrs. Meena Sharma',
      age: 45,
      location: 'Jaipur, Rajasthan',
      rating: 5,
      date: 'January 2024',
      procedure: 'Spinal Fusion',
      doctor: 'Dr. Amit Singh',
      avatar: '👩',
      testimonial: 'My chronic back pain was ruining my quality of life. Dr. Amit Singh performed minimally invasive spinal fusion surgery, and the results are incredible. The pain is completely gone, and I can enjoy activities with my family again.',
      highlights: ['Pain relief', 'Minimally invasive', 'Quality of life improved'],
      verified: true,
      videoAvailable: true
    },
    {
      id: 4,
      name: 'Master Rohan Gupta',
      age: 10,
      location: 'Pune, Maharashtra',
      rating: 5,
      date: 'December 2023',
      procedure: 'Clubfoot Treatment',
      doctor: 'Dr. Meera Patel',
      avatar: '👦',
      testimonial: 'Our son had clubfoot, and we were very worried. Dr. Meera Patel was so gentle and explained everything clearly. The treatment worked perfectly, and now Rohan runs and plays like any other child. Thank you so much!',
      highlights: ['Gentle care', 'Clear communication', 'Perfect results'],
      verified: true,
      videoAvailable: false,
      parentReview: true
    },
    {
      id: 5,
      name: 'Mr. Suresh Reddy',
      age: 52,
      location: 'Hyderabad, Telangana',
      rating: 5,
      date: 'November 2023',
      procedure: 'Shoulder Arthroscopy',
      doctor: 'Dr. Priya Kumari',
      avatar: '👨',
      testimonial: 'My frozen shoulder was causing severe pain and limiting my daily activities. The arthroscopic surgery was performed flawlessly, and the physiotherapy program was excellent. I have full range of motion back!',
      highlights: ['Flawless surgery', 'Full recovery', 'Excellent physiotherapy'],
      verified: true,
      videoAvailable: false
    },
    {
      id: 6,
      name: 'Mrs. Kavita Singh',
      age: 38,
      location: 'Chandigarh, India',
      rating: 5,
      date: 'October 2023',
      procedure: 'Knee Arthroscopy',
      doctor: 'Dr. Priya Kumari',
      avatar: '👩',
      testimonial: 'I had a meniscus tear that was preventing me from my daily runs. Dr. Priya performed arthroscopic surgery, and the recovery was much faster than I expected. I am back to running marathons now!',
      highlights: ['Fast recovery', 'Back to running', 'Exceeded expectations'],
      verified: true,
      videoAvailable: true
    },
    {
      id: 7,
      name: 'Mr. Deepak Joshi',
      age: 42,
      location: 'Ahmedabad, Gujarat',
      rating: 4,
      date: 'September 2023',
      procedure: 'Fracture Treatment',
      doctor: 'Dr. Rajesh Sharma',
      avatar: '👨',
      testimonial: 'After my accident, I had multiple fractures. The emergency care was prompt and professional. Dr. Sharma handled my case with great skill, and I have recovered completely without any complications.',
      highlights: ['Prompt emergency care', 'Complete recovery', 'No complications'],
      verified: true,
      videoAvailable: false
    },
    {
      id: 8,
      name: 'Mrs. Anita Agarwal',
      age: 55,
      location: 'Kolkata, West Bengal',
      rating: 5,
      date: 'August 2023',
      procedure: 'Physiotherapy',
      doctor: 'Dr. Suresh Kumar',
      avatar: '👩',
      testimonial: 'The physiotherapy team is outstanding. After my knee surgery, Dr. Suresh Kumar designed a perfect rehabilitation plan. The facilities are modern, and the care is personalized. Highly recommended!',
      highlights: ['Outstanding team', 'Personalized care', 'Modern facilities'],
      verified: true,
      videoAvailable: false
    }
  ];

  const filteredTestimonials = testimonials;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const averageRating = (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1);

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0f1c3f] via-[#132a63] to-[#0c1a3a] text-white py-20">
        {/* Base overlay for contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-[#0f1c3f]/70 to-[#0a1733]/80 pointer-events-none" />
        {/* Soft glows similar to Specialty hero */}
        <div className="absolute inset-0 opacity-60 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 16% 18%, rgba(99,180,255,0.32), transparent 26%),
                            radial-gradient(circle at 80% 12%, rgba(255,255,255,0.12), transparent 32%),
                            radial-gradient(circle at 70% 82%, rgba(79,140,255,0.20), transparent 34%)`
        }} />
        {/* Subtle grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)`,
          backgroundSize: '160px 160px'
        }} />
        {/* Floating highlights */}
        <div className="absolute -left-10 top-10 w-40 h-40 bg-blue-400/25 blur-3xl animate-floatSlow pointer-events-none" />
        <div className="absolute right-10 bottom-10 w-48 h-48 bg-cyan-300/20 blur-3xl animate-floatSlowDel pointer-events-none" />
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/12 border border-white/25 text-sm font-semibold shadow-lg backdrop-blur animate-pulseGlow">
              ⭐ Trusted outcomes from real patients
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight drop-shadow-sm">
              Patient Testimonials
            </h1>
            <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
              Read what our patients have to say about their experience at Sharma Ortho Center. Their stories inspire us to continue providing exceptional orthopedic care.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-blue-50/90">
              <span className="px-3 py-1 rounded-full border border-white/20 bg-white/10">Joint Reconstruction • Sports Rehab • Spine Care</span>
              <span className="px-3 py-1 rounded-full border border-white/20 bg-white/10">Emergency Trauma • Pediatric Orthopedics</span>
            </div>
          </div>
        </div>
      </section>

      {/* Appreciation Highlights */}
      <section className="py-14 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center space-y-3">
            <p className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-sm font-semibold text-blue-700">
              What patients love most
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Care they keep mentioning</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              The themes that show up in nearly every story—precision surgery, supportive rehab, and warm bedside care.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Precise procedures', desc: 'Joint replacement, arthroscopy, and fracture care with evidence-backed protocols.', icon: '🦴' },
              { title: 'Rehab that restores', desc: 'Sports medicine and physiotherapy that get patients back to what they love.', icon: '🧘' },
              { title: 'Human-first care', desc: 'Clear communication, empathy, and coordinated teams across every specialty.', icon: '🤝' }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-blue-50/60 border border-blue-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Snapshot */}
      <section className="py-14 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center space-y-3">
            <p className="inline-flex items-center px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-sm font-semibold text-emerald-700">
              How their journey feels
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">From first consult to full recovery</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              A coordinated path—triage, expert surgery, guided rehab, and long-term follow-up.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { title: 'Arrival & triage', desc: 'Rapid assessment with imaging and senior consults.', icon: '⏱️' },
              { title: 'Evidence-led surgery', desc: 'Protocols tailored to joint, spine, sports or trauma needs.', icon: '🛠️' },
              { title: 'Rehab & return', desc: 'Sports med + physio plans to restore strength and mobility.', icon: '🏃' },
              { title: 'Follow-up & support', desc: 'Recovery milestones tracked with ongoing guidance.', icon: '✅' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all">
                <div className="text-2xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes floatSlow { 0% { transform: translateY(0); } 50% { transform: translateY(-12px); } 100% { transform: translateY(0); } }
        @keyframes floatSlowDel { 0% { transform: translateY(0); } 50% { transform: translateY(10px); } 100% { transform: translateY(0); } }
        @keyframes pulseGlow { 0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.25);} 70% { box-shadow: 0 0 0 12px rgba(255,255,255,0);} 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0);} }
        .animate-floatSlow { animation: floatSlow 14s ease-in-out infinite; }
        .animate-floatSlowDel { animation: floatSlowDel 16s ease-in-out infinite; }
        .animate-pulseGlow { animation: pulseGlow 3s ease-in-out infinite; }
      `}</style>

      {/* Overview Stats */}
      <section className="py-14 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-white to-blue-50 border border-blue-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex">{renderStars(5)}</div>
                <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-full">Top Rated</span>
              </div>
              <div className="text-4xl font-extrabold text-primary-700">{averageRating}</div>
              <p className="text-sm text-gray-600 mt-1">Average Rating</p>
            </div>
            <div className="bg-gradient-to-br from-white to-emerald-50 border border-emerald-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <ThumbsUp className="h-8 w-8 text-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">Quality</span>
              </div>
              <div className="text-4xl font-extrabold text-emerald-700">98%</div>
              <p className="text-sm text-gray-600 mt-1">Patient Satisfaction</p>
            </div>
            <div className="bg-gradient-to-br from-white to-purple-50 border border-purple-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <User className="h-8 w-8 text-purple-600" />
                <span className="text-xs font-semibold text-purple-700 bg-purple-100 px-2 py-1 rounded-full">Voices</span>
              </div>
              <div className="text-4xl font-extrabold text-purple-700">{testimonials.length}+</div>
              <p className="text-sm text-gray-600 mt-1">Patient Reviews</p>
            </div>
            <div className="bg-gradient-to-br from-white to-amber-50 border border-amber-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <Quote className="h-8 w-8 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">Verified</span>
              </div>
              <div className="text-4xl font-extrabold text-amber-700">100%</div>
              <p className="text-sm text-gray-600 mt-1">Verified Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredTestimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="card bg-white border border-gray-100 rounded-3xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 p-6 flex flex-col gap-5"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-50 border border-primary-100 rounded-full flex items-center justify-center text-2xl">
                      {testimonial.avatar}
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-gray-900 text-lg leading-tight">
                        {testimonial.name}
                        {testimonial.parentReview && <span className="text-sm text-gray-500 ml-1">(Parent)</span>}
                      </h3>
                      <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {testimonial.location}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span>Age {testimonial.age}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {testimonial.verified && (
                      <span className="bg-green-100 text-green-800 text-xs px-2.5 py-1 rounded-full font-semibold border border-green-200">
                        Verified
                      </span>
                    )}
                    {testimonial.videoAvailable && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-200">
                        Video
                      </span>
                    )}
                  </div>
                </div>

                {/* Rating and Date */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center text-yellow-400">{renderStars(testimonial.rating)}</div>
                    <span className="text-gray-600 font-semibold">{testimonial.rating}.0</span>
                  </div>
                  <div className="flex items-center text-gray-500 gap-1">
                    <Calendar className="h-4 w-4" />
                    {testimonial.date}
                  </div>
                </div>

                {/* Treatment Info */}
                <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 space-y-2">
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold text-primary-800">Procedure: </span>
                    {testimonial.procedure}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold text-primary-800">Treated by: </span>
                    {testimonial.doctor}
                  </div>
                </div>

                {/* Testimonial Text */}
                <div className="relative">
                  <Quote className="absolute -top-3 -left-3 h-8 w-8 text-primary-100" />
                  <p className="text-gray-700 leading-relaxed italic pl-6">
                    "{testimonial.testimonial}"
                  </p>
                </div>

                {/* Highlights */}
                <div className="flex flex-wrap gap-2">
                  {testimonial.highlights.map((highlight, index) => (
                    <span
                      key={index}
                      className="bg-green-50 text-green-800 border border-green-100 text-xs px-2.5 py-1 rounded-full font-semibold"
                    >
                      ✓ {highlight}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Leave a Review Section */}
      <section className="py-16 bg-gradient-to-r from-primary-700 via-primary-600 to-blue-700 text-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-5xl mx-auto bg-white/10 border border-blue-200/30 rounded-3xl px-6 py-10 md:px-10 md:py-12 shadow-2xl backdrop-blur overflow-hidden">
            <div className="absolute -right-12 -top-10 w-48 h-48 bg-white/10 blur-3xl pointer-events-none animate-floatSlow" />
            <div className="absolute -left-10 bottom-0 w-40 h-40 bg-cyan-300/10 blur-3xl pointer-events-none animate-floatSlowDel" />
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1 text-left">
                <p className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-blue-200/40 text-sm font-semibold mb-4">
                  <Quote className="mr-2 h-4 w-4" />
                  Share Your Experience
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Your story can inspire someone else to take the first step.
                </h2>
                <p className="text-blue-100 text-base md:text-lg mb-6">
                  Help other patients feel confident about their treatment choices by sharing your recovery journey and experience with our team.
                </p>
                <div className studio="flex flex-col sm:flex-row gap-4">
                  <button className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-yellow-400 text-primary-900 font-semibold shadow-lg hover:bg-yellow-300 transition-colors">
                    <Quote className="mr-2 h-5 w-5" />
                    Leave a Review
                  </button>
                  <button className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 text-white font-semibold hover:bg-white/10 transition-colors">
                    <User className="mr-2 h-5 w-5" />
                    Record Video Testimonial
                  </button>
                </div>
              </div>
              <div className="w-full max-w-sm bg-white/8 rounded-2xl border border-blue-200/50 p-5 shadow-lg">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <ThumbsUp className="h-5 w-5 mr-2 text-yellow-300" />
                  Why your feedback matters
                </h3>
                <ul className="space-y-2 text-sm text-blue-100">
                  <li className="flex items-start gap-2"><span className="text-yellow-200">•</span>Helps other patients choose the right treatment and doctor.</li>
                  <li className="flex items-start gap-2"><span className="text-yellow-200">•</span>Guides us to continually improve our services and care.</li>
                  <li className="flex items-start gap-2"><span className="text-yellow-200">•</span>Celebrates your courage and recovery with our community.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Integration */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-5xl mx-auto overflow-hidden rounded-3xl p-10 md:p-12 border border-gray-100 shadow-xl bg-white">
            <div className="absolute -left-8 top-6 w-36 h-36 bg-yellow-200/30 blur-3xl pointer-events-none animate-floatSlow" />
            <div className="absolute right-0 bottom-0 w-40 h-40 bg-blue-200/25 blur-3xl pointer-events-none animate-floatSlowDel" />
            <div className="relative text-center space-y-4">
              <p className="inline-flex items-center px-4 py-1.5 rounded-full bg-yellow-50 border border-yellow-200 text-xs font-semibold text-yellow-800 shadow-sm">
                <span className="mr-2">★</span>
                Find us on Google Reviews
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Loved your care at Sharma Ortho Center?
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                Join hundreds of patients who have shared their success stories on Google and help others discover trusted orthopedic care.
              </p>
              <button className="inline-flex items-center justify-center px-10 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-300 text-primary-900 font-semibold shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5">
                <span className="mr-2 text-xl">🌟</span>
                View Google Reviews
              </button>
              <div className="flex justify-center gap-4 text-sm text-gray-500">
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">Verified patient voices</span>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">Across joint, spine, sports & trauma</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
